import type { FieldsOrder } from "src/components/Form/Form.types.ts";
import { Panel } from "src/components/Panel/Panel.tsx";
import React, { type HTMLAttributes } from "react";
import type { FieldValues } from "react-hook-form";
import { useResourceFormTranslation } from "src/components/Form/hooks/useResourceFormTranslation.ts";
import { TooltipV2 } from "src/components/TooltipV2/TooltipV2.tsx";
import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { PanelField } from "src/components/Form/panels/DefaultFormPanel/PanelField.tsx";

export type DefaultFormPanelProps<
  FormValues extends FieldValues = FieldValues,
> = {
  title?: string;
  helperText?: string;
  panelKey: string;
  fields_order: FieldsOrder<FormValues>;
} & HTMLAttributes<HTMLDivElement>;

export function DefaultFormPanel<FormValues extends FieldValues = FieldValues>(
  props: DefaultFormPanelProps<FormValues>,
) {
  const { fields_order, title, panelKey, helperText, ...restProps } = props;
  const { t } = useResourceFormTranslation();

  return (
    <Panel {...restProps}>
      <Panel.Header>
        <Panel.Title>
          {title ?? t(`form.panel.${panelKey}.title`)}
          {helperText && (
            <TooltipV2 cursor={"help"} content={helperText}>
              <i className={"icon-15-info"} css={iconStyle} />
            </TooltipV2>
          )}
        </Panel.Title>
      </Panel.Header>
      <Panel.Content>
        {(fields_order || []).map((name) => {
          const key = typeof name === "string" ? name : JSON.stringify(name);
          return <PanelField name={name} key={key} />;
        })}
      </Panel.Content>
    </Panel>
  );
}

const iconStyle = (theme: ExactTheme) => css`
  color: ${theme.colorsV2.blue[500]};
  font-size: 15px;
`;
